'use client';

import { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { ParticlesBackground } from '@/components/ui/ParticlesBackground';

const MultiAgentNetwork = dynamic(
  () => import('@/components/animations/MultiAgentNetwork'),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full w-full items-center justify-center text-white/40 text-xs font-mono">
        scene…
      </div>
    ),
  }
);

type Tag = 'generative' | 'ascii' | 'dither' | 'dot' | 'interactive' | 'manifesto' | 'shipped' | 'math';

interface Entry {
  id: string;
  title: string;
  kind: string;
  blurb: string;
  tags: Tag[];
  tunableHref?: string;
  conceptHref?: string;
  preview: React.ReactNode;
}

const entries: Entry[] = [
  // ── React pieces (built-in) ─────────────────────────────────────
  {
    id: 'agent-network',
    title: 'agent_network',
    kind: 'three.js · scene',
    blurb:
      'A spatial diagram of our delivery model — Flutter, Next.js, Python, Rust, and a vector database, each rendered as a glowing node.',
    tags: ['generative', 'shipped'],
    preview: (
      <div className="h-full w-full">
        <MultiAgentNetwork activeSection={-1} />
      </div>
    ),
  },
  {
    id: 'particle-field',
    title: 'particle_field',
    kind: 'canvas · ambient',
    blurb:
      'The quiet ambient field that drifts behind several pages. Tuned for atmosphere, not attention.',
    tags: ['generative', 'shipped'],
    preview: (
      <div className="absolute inset-0 overflow-hidden">
        <ParticlesBackground
          className="absolute inset-0 h-full w-full pointer-events-none"
          color="rgba(84, 197, 248, 0.45)"
          particleCount={70}
          connectLines
        />
      </div>
    ),
  },
  // ── /secret HTML pieces (iframe-embedded) ───────────────────────
  iframeEntry('substrate', 'substrate', 'a seeded flow field — invisible currents made visible through 2400 drifting particles.', ['generative']),
  iframeEntry('built-for-people', 'built_for_people', 'full brand composition: dithered portrait, typed headline, terminal workflow, mountain footer.', ['manifesto', 'dither', 'ascii']),
  iframeEntry('quiet-mountain', 'quiet_mountain', 'a generative ascii landscape. the green ridge marks the silhouette as it drifts.', ['ascii']),
  iframeEntry('dither-portrait', 'dither_portrait', 'a cinematic portrait emerges from bayer noise, holds, dissolves, repeats.', ['dither', 'manifesto']),
  iframeEntry('terminal-manifesto', 'terminal_manifesto', 'the brand manifesto types itself out over a responsive ascii field.', ['ascii', 'manifesto', 'interactive']),
  iframeEntry('signal-field', 'signal_field', 'move your cursor through a flowing ascii field — the green halo follows.', ['ascii', 'interactive']),
  iframeEntry('data-to-intelligence', 'data_to_intelligence', 'ascii portrait + mountains condensing from noise. light mode hero composition.', ['ascii', 'manifesto']),
  iframeEntry('pocket', 'pocket', 'an ascii phone home screen — status bar, app grid, dock. mint notification badge pulses on the top-right app.', ['ascii']),
  iframeEntry('dot-matrix-loader', 'dot_matrix_loader', 'dot-matrix display cycling DATACODE → AGENTS THAT WORK → BUILT FOR HUMANS, assembled from a 5×7 bitmap font.', ['dot']),
  iframeEntry('dot-form-headline', 'dot_form_headline', 'six thousand particles converging into "from data, to intelligence." the headline forms, breathes, peels off, reforms.', ['dot', 'manifesto']),
  iframeEntry('ascii-magic', 'ascii_magic', 'a portrait emerging from noise as ascii art. luminance to glyph — the picture, in characters.', ['ascii', 'manifesto']),
  iframeEntry('dot-fusion', 'dot_fusion', 'three techniques stacked: ascii portrait above, dot-matrix ticker middle, dot-form swarm below.', ['ascii', 'dot', 'manifesto']),
  iframeEntry('arrival-board', 'arrival_board', 'a four-row status board updating on coprime rhythms — boot, agents, signal, status. a working system reporting itself.', ['ascii'], '/secret/_concepts/arrival-board.md'),
  iframeEntry('answer-emerges', 'answer_emerges', 'three shapes, the same matter. signal. becomes meaning. becomes action. the same particles do all the becoming.', ['dot', 'manifesto'], '/secret/_concepts/answer-emerges.md'),
  iframeEntry('ascii-bloom', 'ascii_bloom', 'three thoughts clarify in parallel. three small blooms expand from a triangle of centres, merging into the portrait.', ['ascii', 'manifesto'], '/secret/_concepts/ascii-bloom.md'),
  iframeEntry('dot-clients', 'dot_clients', 'the real portfolio in motion: datacode → parezar → taakline → dila/menu → kurdish/calendar → datacode. brand anchor glows mint on every return.', ['dot', 'manifesto']),
  iframeEntry('dither-logo', 'dither_logo', 'the datacode wordmark dithers in from noise, holds, dissolves, repeats. data. code. shipped.', ['dither', 'manifesto']),
  iframeEntry('silent-wing', 'silent_wing', 'homage to yuruyurau (#つぶやきProcessing, may 2026). a dense field whose entire form is one tweet-length equation. pure math, no chrome.', ['math']),
  iframeEntry('silent-spiral', 'silent_spiral', 'after hisadan, cc by-sa. a phyllotaxis spiral with r-mod-t brightness banding that breathes between fine and wide.', ['math']),
];

function iframeEntry(slug: string, title: string, blurb: string, tags: Tag[], conceptHref?: string): Entry {
  return {
    id: slug,
    title,
    kind: tagsToKind(tags),
    blurb,
    tags,
    tunableHref: `/secret/${slug}/index.html`,
    conceptHref,
    preview: (
      <iframe
        src={`/secret/${slug}/index.html`}
        title={title}
        loading="lazy"
        className="absolute inset-0 h-full w-full border-0"
        sandbox="allow-scripts allow-same-origin"
      />
    ),
  };
}

function tagsToKind(tags: Tag[]): string {
  if (tags.includes('dither')) return 'html · dither';
  if (tags.includes('dot')) return 'html · dot matrix';
  if (tags.includes('ascii')) return 'html · ascii';
  return 'html · generative';
}

const ALL_TAGS: { key: 'all' | Tag; label: string }[] = [
  { key: 'all', label: 'all' },
  { key: 'math', label: 'math' },
  { key: 'generative', label: 'generative' },
  { key: 'ascii', label: 'ascii' },
  { key: 'dither', label: 'dither' },
  { key: 'dot', label: 'dot' },
  { key: 'interactive', label: 'interactive' },
  { key: 'manifesto', label: 'manifesto' },
  { key: 'shipped', label: 'shipped' },
];

export function LabPageClient() {
  const [filter, setFilter] = useState<'all' | Tag>('all');
  const filtered = useMemo(
    () => (filter === 'all' ? entries : entries.filter((e) => e.tags.includes(filter))),
    [filter]
  );

  return (
    <div className="bg-[#0c0c0c] text-white min-h-screen">
      <section className="px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-12 max-w-7xl mx-auto">
        <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/40 mb-5">
          Datacode · The Lab · {entries.length} pieces
        </div>
        <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
          Everything we&apos;ve built,
          <br />
          <span className="text-white/55">in one screen.</span>
        </h1>
        <p className="text-white/60 text-base md:text-lg mt-8 max-w-2xl leading-relaxed">
          Generative studies, ascii experiments, dither portraits, dot-matrix
          tickers, and the secret animations from the homepage — all live, all
          in the browser. Click any tile to open the standalone version.
        </p>

        <div className="flex flex-wrap gap-2 mt-10">
          {ALL_TAGS.map((t) => {
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded border transition ${
                  active
                    ? 'border-[#54C5F8] text-[#54C5F8] bg-[#54C5F8]/10'
                    : 'border-white/10 text-white/55 hover:border-white/30 hover:text-white/80'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {filtered.map((entry) => (
            <article
              key={entry.id}
              className="group rounded-xl overflow-hidden bg-[#0c0c0c] border border-white/5 hover:border-white/15 transition-colors shadow-[0_30px_60px_-30px_rgba(0,0,0,0.7)]"
            >
              <a
                href={entry.tunableHref ?? '#'}
                target={entry.tunableHref ? '_blank' : undefined}
                rel={entry.tunableHref ? 'noopener noreferrer' : undefined}
                className="block"
              >
                <div className="relative aspect-[16/10] bg-[#0c0c0c] overflow-hidden">
                  {entry.preview}
                </div>
              </a>
              <div className="p-5 md:p-6">
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <h2 className="font-mono text-base md:text-lg text-white">
                    {entry.title}
                  </h2>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-white/40 shrink-0">
                    {entry.kind}
                  </div>
                </div>
                <p className="text-white/55 text-sm leading-relaxed mb-4">
                  {entry.blurb}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {entry.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-mono text-[10px] uppercase tracking-wider text-white/45 border border-white/10 rounded px-2 py-0.5"
                    >
                      {tag}
                    </span>
                  ))}
                  <div className="ml-auto flex items-center gap-3">
                    {entry.conceptHref && (
                      <a
                        href={entry.conceptHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-white/45 hover:text-white/80 transition"
                      >
                        concept ↗
                      </a>
                    )}
                    {entry.tunableHref && (
                      <a
                        href={entry.tunableHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-[#54C5F8] hover:text-[#7CFFB2] transition"
                      >
                        open ↗
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto pb-24 border-t border-white/10 pt-12">
        <p className="text-white/45 text-sm md:text-base leading-relaxed max-w-2xl">
          Built in Erbil by the team at Datacode. Generative React studies live
          under{' '}
          {/* Static files in public/, not Next routes, so a plain <a> is correct. */}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a
            href="/algorithmic-art/"
            className="text-white/70 underline decoration-dotted underline-offset-4 hover:text-white"
          >
            /algorithmic-art
          </a>
          . The HTML/ASCII/dither pieces and their recorder pipeline live under{' '}
          {/* Static files in public/, not Next routes, so a plain <a> is correct. */}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a
            href="/secret/index.html"
            className="text-white/70 underline decoration-dotted underline-offset-4 hover:text-white"
          >
            /secret
          </a>
          .
        </p>
      </section>
    </div>
  );
}
